import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { platformLabel, type Platform } from "@/platform/Platform";
import { GameConfig } from "@/config/GameConfig";
import { LevelRegistry } from "@/levels/LevelRegistry";
import { WorldRegistry } from "@/levels/WorldRegistry";
import type { LevelDefinition } from "@/levels/types";
import type { WorldDefinition } from "@/levels/worldTypes";
import { createButton } from "@/ui/Button";
import { drawStar } from "@/ui/Star";
import { getLeaderboardService } from "@/online/LeaderboardService";
import { OnlineFeaturesStorage } from "@/online/OnlineFeaturesStorage";
import { flushOnlineQueues } from "@/online/bootstrap";
import { createOnlineFeaturesModal, type OnlineFeaturesModalHandle } from "@/ui/OnlineFeaturesModal";
import { createDataPrivacyModal, type DataPrivacyModalHandle } from "@/ui/DataPrivacyModal";
import { createNameEntryModal, type NameEntryModalHandle } from "@/ui/NameEntryModal";
import type { LevelLeaderboardEntry, MyLevelRank, MyWorldRank, WorldLeaderboardEntry } from "@/online/types";
import { drawWorldIcon } from "@/ui/worldGraphics";
import {
  computeLeaderboardRegions,
  worldIconSlot,
  desiredTitleSize,
  LB_TAB_ROW_HEIGHT,
  LB_LEVEL_PILL_SIZE,
  LB_LEVEL_PILL_GAP,
  LB_LEVEL_PILL_HIT_SIZE,
  LB_WORLD_ICON_GAP,
  LB_TITLE_TEXT,
} from "@/ui/leaderboardLayout";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

export interface LeaderboardSceneData {
  levelId?: string;
  returnScene?: string;
  returnData?: Record<string, unknown>;
}

type Tab = "level" | "world";
type LoadState = "loading" | "ok" | "empty" | "offline" | "error";

const ROW_HEIGHT = 34;
const LIST_WIDTH_MAX = 460;

/**
 * The one screen for both Top 10 (per level) and world standings (per
 * world), reachable from the main menu and the level-complete overlay.
 * Talks only to `LeaderboardService` (never Supabase/`fetch` directly),
 * so it renders identically whether that service is the real online one,
 * the offline stub, or (in tests) `MockLeaderboardService`.
 */
export class LeaderboardScene extends Phaser.Scene {
  private returnScene = "MenuScene";
  private returnData: Record<string, unknown> = {};
  private tab: Tab = "level";
  private world!: WorldDefinition;
  private level!: LevelDefinition;
  private requestToken = 0;

  private loadState: LoadState = "loading";
  private errorMessage = "";
  private levelEntries: LevelLeaderboardEntry[] = [];
  private worldEntries: WorldLeaderboardEntry[] = [];
  private myLevelRank: MyLevelRank | null = null;
  private myWorldRank: MyWorldRank | null = null;
  private myDisplayName: string | null = null;
  /** PR34: see `create()`. */
  private platform: Platform = "desktop";

  private mainContainer!: Phaser.GameObjects.Container;
  private onlineModal!: OnlineFeaturesModalHandle;
  private privacyModal!: DataPrivacyModalHandle;
  private nameModal!: NameEntryModalHandle;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("LeaderboardScene");
  }

  create(data: LeaderboardSceneData): void {
    this.returnScene = data.returnScene ?? "MenuScene";
    this.returnData = data.returnData ?? {};
    const requestedLevel = data.levelId ? LevelRegistry.byId(data.levelId) : undefined;
    this.level = requestedLevel ?? LevelRegistry.firstLevel();
    this.world = WorldRegistry.byId(this.level.worldId) ?? WorldRegistry.all()[0];
    // PR34: the viewer's own Mobile/Desktop board, decided once per visit -
    // level/world lists and "my rank" all read it. No toggle.
    this.platform = detectPlatform();
    this.tab = "level";
    this.requestToken = 0;
    this.loadState = "loading";
    this.levelEntries = [];
    this.worldEntries = [];
    this.myLevelRank = null;
    this.myWorldRank = null;
    this.myDisplayName = null;

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);

    this.privacyModal = createDataPrivacyModal(this, 95, () => this.privacyModal.setVisible(false));
    this.onlineModal = createOnlineFeaturesModal(this, 90, {
      onEnable: () => this.handleEnableOnline(),
      onPlayOffline: () => this.handlePlayOffline(),
      onDataPrivacy: () => this.privacyModal.setVisible(true),
    });
    this.nameModal = createNameEntryModal(this, 96, {
      onSave: (name) => this.handleSaveName(name),
      onNotNow: () => {},
    });

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.input.on("pointermove", this.onListPointerMove, this);
    this.input.on("pointerup", this.onListPointerUp, this);
    this.input.on("pointerupoutside", this.onListPointerUp, this);
    this.input.on("wheel", this.onListWheel, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    if (!OnlineFeaturesStorage.hasAsked()) {
      this.onlineModal.setVisible(true);
    } else {
      void this.refresh();
    }
  }

  private teardown(): void {
    this.scale.off("resize", this.onResize, this);
    this.input.off("pointermove", this.onListPointerMove, this);
    this.input.off("pointerup", this.onListPointerUp, this);
    this.input.off("pointerupoutside", this.onListPointerUp, this);
    this.input.off("wheel", this.onListWheel, this);
    this.onlineModal.destroy();
    this.privacyModal.destroy();
    this.nameModal.destroy();
    this.listMask?.destroy();
  }

  private onListPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.listDragging || this.listMaxScroll <= 0) return;
    const delta = pointer.y - this.listDragStartPointerY;
    this.listScrollY = Phaser.Math.Clamp(this.listDragStartScroll - delta, 0, this.listMaxScroll);
    this.applyListScroll();
  }

  private onListPointerUp(): void {
    this.listDragging = false;
  }

  private onListWheel(_pointer: unknown, _objects: unknown, _dx: number, dy: number): void {
    if (this.listMaxScroll <= 0) return;
    this.listScrollY = Phaser.Math.Clamp(this.listScrollY + dy * 0.5, 0, this.listMaxScroll);
    this.applyListScroll();
  }

  private onResize(): void {
    this.buildLayout();
    this.onlineModal.relayout();
    this.privacyModal.relayout();
    this.nameModal.relayout();
  }

  private goBack(): void {
    this.scene.start(this.returnScene, this.returnData);
  }

  // ---------------------------------------------------------------------
  // ONLINE FEATURES / name handlers
  // ---------------------------------------------------------------------

  private handleEnableOnline(): void {
    OnlineFeaturesStorage.setEnabled(true);
    this.onlineModal.setVisible(false);
    // PR34: flushes BOTH queues (Campaign and Endless) - results captured on
    // this device before the opt-in are sent now, once a valid session exists.
    void flushOnlineQueues();
    void this.refresh();
  }

  private handlePlayOffline(): void {
    OnlineFeaturesStorage.setEnabled(false);
    this.onlineModal.setVisible(false);
    void this.refresh();
  }

  private async handleSaveName(name: string): Promise<{ ok: boolean; error?: string }> {
    const result = await getLeaderboardService().setDisplayName(name);
    if (result.state !== "ok") {
      return { ok: false, error: result.state === "error" ? result.message : "Could not save name right now." };
    }
    this.myDisplayName = result.data;
    // A full refresh, not just renderList(): the already-fetched Top 10
    // rows (this.levelEntries/worldEntries) were pulled before the name
    // change and still show "ANONYMOUS" for this player's own entry if
    // they're in the list - only a fresh fetch replaces that with the
    // name just saved.
    void this.refresh();
    return { ok: true };
  }

  private openEditName(): void {
    this.nameModal.open({ headline: "EDIT NAME", initialValue: this.myDisplayName ?? "" });
  }

  // ---------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------

  private async refresh(): Promise<void> {
    const token = ++this.requestToken;
    this.loadState = "loading";
    this.renderList();

    const service = getLeaderboardService();
    if (!service.isAvailable()) {
      if (token !== this.requestToken) return;
      this.loadState = "offline";
      this.renderList();
      return;
    }

    if (this.tab === "level") {
      const [entriesResult, rankResult, nameResult] = await Promise.all([
        service.getLevelLeaderboard(this.level.id, this.platform),
        service.getMyLevelRank(this.level.id, this.platform),
        service.getMyDisplayName(),
      ]);
      if (token !== this.requestToken) return;

      if (nameResult.state === "ok") this.myDisplayName = nameResult.data;
      if (entriesResult.state === "ok") {
        this.levelEntries = entriesResult.data;
        this.myLevelRank = rankResult.state === "ok" ? rankResult.data : null;
        this.loadState = entriesResult.data.length === 0 ? "empty" : "ok";
      } else {
        this.loadState = entriesResult.state === "offline" ? "offline" : "error";
        this.errorMessage = entriesResult.state === "error" ? entriesResult.message : "";
      }
    } else {
      const [entriesResult, rankResult, nameResult] = await Promise.all([
        service.getWorldLeaderboard(this.world.id, this.platform),
        service.getMyWorldRank(this.world.id, this.platform),
        service.getMyDisplayName(),
      ]);
      if (token !== this.requestToken) return;

      if (nameResult.state === "ok") this.myDisplayName = nameResult.data;
      if (entriesResult.state === "ok") {
        this.worldEntries = entriesResult.data;
        this.myWorldRank = rankResult.state === "ok" ? rankResult.data : null;
        this.loadState = entriesResult.data.length === 0 ? "empty" : "ok";
      } else {
        this.loadState = entriesResult.state === "offline" ? "offline" : "error";
        this.errorMessage = entriesResult.state === "error" ? entriesResult.message : "";
      }
    }

    this.renderList();
  }

  // ---------------------------------------------------------------------
  // Layout: header / tabs / selectors (rebuilt on tab/world/level change and resize)
  // ---------------------------------------------------------------------

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    const { width, height } = this.scale;
    const centerX = width / 2;
    const worldCount = WorldRegistry.all().length;

    // PR27 human-QA fix: every region's Y now comes from this one
    // computation (see `leaderboardLayout.ts`'s own doc for the overlap
    // bug this replaces) instead of each region guessing where the
    // previous one ended. First pass: only the header's own layout is
    // needed to place the title/BACK - it doesn't depend on the title's
    // own height.
    const initialRegions = computeLeaderboardRegions(width, height, this.tab, worldCount);

    // Mobile beta fix (PR29, 3rd round): BACK and the title no longer
    // fight over a horizontal safe zone on mobile at all - below
    // `LB_MOBILE_HEADER_BREAKPOINT` (`computeHeaderLayout()`), BACK gets
    // its own row and the title gets its own row below it, centered, with
    // the FULL content width available (`titleMaxWidth`). Human QA found
    // the previous shrink-then-wrap approach (still present on desktop,
    // where title/BACK do still share a row) produced readable-but-ugly
    // results on mobile even once it stopped overlapping BACK - this
    // gives the title enough room that "LEADERBOARD" never needs to
    // shrink or wrap in practice. `wordWrap` is kept purely as a defensive
    // backstop (never actually exercised by this title at any supported
    // width), not the primary mechanism.
    const titleSize = desiredTitleSize(width);
    const title = this.add
      .text(centerX, initialRegions.titleY, LB_TITLE_TEXT, {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
        align: "center",
        wordWrap: { width: initialRegions.titleMaxWidth, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.mainContainer.add(title);

    // Second pass: recompute every region below the header from the
    // title's REAL measured height - a no-op whenever it rendered on one
    // line (comfortably under the conservative single-line estimate
    // `computeLeaderboardRegions()` otherwise assumes; the common case at
    // every supported width), but growing the header's reserved space in
    // the rare case wordWrap's defensive backstop actually kicked in, so
    // the tab row (and everything after it) can never start on top of a
    // wrapped title.
    const regions = computeLeaderboardRegions(width, height, this.tab, worldCount, title.height);

    const backButton = createButton(this, regions.backX, regions.backY, "BACK", () => this.goBack(), {
      width: regions.backWidth,
      height: regions.backHeight,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(backButton);

    this.buildTabRow(centerX, regions.tabRowY);
    this.buildWorldSelector(centerX, regions.worldSelectorY, worldCount);

    if (this.tab === "level" && regions.levelSelectorY !== null) {
      this.buildLevelRow(centerX, regions.levelSelectorY);
    }

    const listTop = regions.contentTop;
    const footerHeight = 56;
    const listBottom = height - footerHeight - 8;
    this.buildListArea(centerX, listTop, Math.max(80, listBottom - listTop));
    this.buildFooter(centerX, height - footerHeight / 2);

    this.renderList();
  }

  private buildTabRow(centerX: number, y: number): void {
    const tabWidth = 130;
    const gap = 8;
    const levelTab = createButton(this, centerX - tabWidth / 2 - gap / 2, y, "LEVEL", () => this.setTab("level"), {
      width: tabWidth,
      height: LB_TAB_ROW_HEIGHT,
      fontSize: "15px",
      fillColor: this.tab === "level" ? GameConfig.colors.accent : 0x1c2030,
      hoverColor: this.tab === "level" ? GameConfig.colors.accentHover : 0x262c40,
      textColor: this.tab === "level" ? "#0f1117" : "#f5f7fa",
    });
    const worldTab = createButton(this, centerX + tabWidth / 2 + gap / 2, y, "WORLD", () => this.setTab("world"), {
      width: tabWidth,
      height: LB_TAB_ROW_HEIGHT,
      fontSize: "15px",
      fillColor: this.tab === "world" ? GameConfig.colors.accent : 0x1c2030,
      hoverColor: this.tab === "world" ? GameConfig.colors.accentHover : 0x262c40,
      textColor: this.tab === "world" ? "#0f1117" : "#f5f7fa",
    });
    this.mainContainer.add([levelTab, worldTab]);
  }

  /**
   * PR27 human-QA fix: the world selector is now this icon-based control
   * at EVERY viewport width - the previous desktop-only text-button/
   * multi-row-wrap variant is removed entirely, not just hidden, per
   * follow-up human QA ("remove the desktop text-name world selector
   * from player-facing UI"). Reuses the game's own `worldGraphics` icon
   * artwork - no new artwork. Each icon is a small Container (badge
   * circle + icon + a >=44px square hit zone) so `Button.ts`'s
   * pill-button styling (which assumes text) doesn't apply here; the
   * selected world gets a filled/accented badge, matching the same
   * "highlighted card" language `WorldSelectScene`'s own cards use.
   * `worldIconSlot()` (see `leaderboardLayout.ts`) caps the icon size on
   * a wide desktop viewport instead of stretching it, so the row reads
   * as one balanced, comfortably-sized control rather than an
   * artificially inflated one.
   */
  private buildWorldSelector(centerX: number, y: number, worldCount: number): void {
    const worlds = WorldRegistry.all();
    const slot = worldIconSlot(this.scale.width, worldCount);
    const totalWidth = worlds.length * slot + (worlds.length - 1) * LB_WORLD_ICON_GAP;
    let x = centerX - totalWidth / 2 + slot / 2;

    for (const world of worlds) {
      const active = world.id === this.world.id;
      const container = this.add.container(x, y);

      const badgeRadius = slot / 2 - 4;
      const badge = this.add.graphics();
      badge.fillStyle(active ? world.theme.primary : 0x1c2030, 1);
      badge.fillCircle(0, 0, badgeRadius);
      badge.lineStyle(active ? 2.5 : 1.5, active ? world.theme.accent : GameConfig.colors.gridLine, active ? 1 : 0.6);
      badge.strokeCircle(0, 0, badgeRadius);
      container.add(badge);

      const iconGraphics = this.add.graphics().setAlpha(active ? 1 : 0.55);
      drawWorldIcon(iconGraphics, world.theme, 0, 0, badgeRadius * 0.68);
      container.add(iconGraphics);

      const hitSize = Math.max(44, slot);
      const hitZone = this.add.zone(0, 0, hitSize, hitSize).setOrigin(0.5).setInteractive({ useHandCursor: true });
      hitZone.on("pointerdown", () => this.setWorld(world));
      container.add(hitZone);

      this.mainContainer.add(container);
      x += slot + LB_WORLD_ICON_GAP;
    }
  }

  private buildLevelRow(centerX: number, y: number): void {
    const group = LevelRegistry.worlds().find((g) => g.world.id === this.world.id);
    const levels = group?.levels ?? [];
    // PR27: pills stay a visually restrained 38x38, padded up to a
    // comfortable ~44px tap target via `minHitWidth`/`minHitHeight` -
    // still smaller than the 46px (pillSize + gap) center-to-center
    // spacing, so adjacent pills' hit areas never overlap.
    const totalWidth = levels.length * LB_LEVEL_PILL_SIZE + (levels.length - 1) * LB_LEVEL_PILL_GAP;
    let x = centerX - totalWidth / 2 + LB_LEVEL_PILL_SIZE / 2;
    for (const level of levels) {
      const active = level.id === this.level.id;
      const btn = createButton(this, x, y, `${level.order}`, () => this.setLevel(level), {
        width: LB_LEVEL_PILL_SIZE,
        height: LB_LEVEL_PILL_SIZE,
        minHitWidth: LB_LEVEL_PILL_HIT_SIZE,
        minHitHeight: LB_LEVEL_PILL_HIT_SIZE,
        fontSize: "14px",
        cornerRadius: 10,
        fillColor: active ? GameConfig.colors.accent : 0x1c2030,
        hoverColor: active ? GameConfig.colors.accentHover : 0x262c40,
        textColor: active ? "#0f1117" : "#8b93a7",
      });
      this.mainContainer.add(btn);
      x += LB_LEVEL_PILL_SIZE + LB_LEVEL_PILL_GAP;
    }
  }

  private setTab(tab: Tab): void {
    if (this.tab === tab) return;
    this.tab = tab;
    this.buildLayout();
    void this.refresh();
  }

  private setWorld(world: WorldDefinition): void {
    if (this.world.id === world.id) return;
    this.world = world;
    const group = LevelRegistry.worlds().find((g) => g.world.id === world.id);
    if (group && group.levels.length > 0) this.level = group.levels[0];
    this.buildLayout();
    void this.refresh();
  }

  private setLevel(level: LevelDefinition): void {
    if (this.level.id === level.id) return;
    this.level = level;
    this.buildLayout();
    void this.refresh();
  }

  // ---------------------------------------------------------------------
  // List area: rebuilt on every data change, independent of buildLayout()
  // ---------------------------------------------------------------------

  private listContainer!: Phaser.GameObjects.Container;
  private listTop = 0;
  private listHeight = 0;
  private listScrollY = 0;
  private listMaxScroll = 0;
  private listMask?: Phaser.GameObjects.Graphics;

  private buildListArea(centerX: number, top: number, height: number): void {
    this.listTop = top;
    this.listHeight = height;
    this.listScrollY = 0;
    this.listContainer = this.add.container(centerX, top);
    this.mainContainer.add(this.listContainer);

    // Scrollable on phone: a drag zone over the viewport (wheel works
    // everywhere too) plus a geometry mask so content never bleeds into
    // the tabs/header above or the footer below.
    const dragZone = this.add.zone(0, top, this.scale.width, height).setOrigin(0, 0).setInteractive();
    dragZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.listDragging = true;
      this.listDragStartPointerY = pointer.y;
      this.listDragStartScroll = this.listScrollY;
    });
    this.mainContainer.add(dragZone);

    this.listMask?.destroy();
    this.listMask = this.make.graphics({});
    this.listMask.fillRect(0, top, this.scale.width, height);
    this.listContainer.setMask(this.listMask.createGeometryMask());
  }

  private listDragging = false;
  private listDragStartPointerY = 0;
  private listDragStartScroll = 0;

  private applyListScroll(): void {
    this.listContainer.y = this.listTop - this.listScrollY;
  }

  private buildFooter(centerX: number, y: number): void {
    const editNameVisible = getLeaderboardService().isAvailable();
    let x = centerX;
    if (editNameVisible) {
      const editButton = createButton(this, centerX - 90, y, "EDIT NAME", () => this.openEditName(), {
        width: 150,
        height: 38,
        fontSize: "13px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      });
      this.mainContainer.add(editButton);
      x = centerX + 90;
    }
    const privacyLink = this.add
      .text(x, y, "DATA & PRIVACY", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: "#5b8cff",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    privacyLink.on("pointerdown", () => this.privacyModal.setVisible(true));
    this.mainContainer.add(privacyLink);
  }

  private renderList(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);
    const width = Math.min(LIST_WIDTH_MAX, this.scale.width - 32);

    // PR34: which board this is - a lightweight label, never a control.
    // Rendered for EVERY list state (loading/offline/error/empty/ok), not
    // just when rows exist: an empty Mobile/Desktop board - the normal state
    // right after launch, since every pre-PR34 row is 'unknown' - must still
    // say which board it is. Top-right of the list, top-anchored at y = 0
    // (origin y 0, never 0.5): the list's geometry mask starts exactly at
    // the container's y = 0, so a vertically-centered label would have its
    // top half masked away. It spans ~y 0-13; status messages start at
    // y = 20, and rows at y = 20.
    const platformTag = this.add
      .text(width / 2, 0, platformLabel(this.platform), { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
      .setOrigin(1, 0);
    this.listContainer.add(platformTag);

    let contentHeight: number;
    switch (this.loadState) {
      case "loading":
        contentHeight = this.renderStatusMessage(width, "LOADING…", []);
        break;
      case "offline":
        contentHeight = this.renderStatusMessage(
          width,
          "LEADERBOARD UNAVAILABLE — YOUR LOCAL PROGRESS IS SAFE",
          OnlineFeaturesStorage.isEnabled() ? ["RETRY"] : ["ENABLE ONLINE FEATURES"],
        );
        break;
      case "error":
        contentHeight = this.renderStatusMessage(width, this.errorMessage || "Something went wrong.", ["RETRY"]);
        break;
      case "empty":
        contentHeight = this.renderStatusMessage(width, "BE THE FIRST", []);
        contentHeight = Math.max(contentHeight, this.renderPersonalRow(width, 60));
        break;
      case "ok":
        contentHeight = this.renderRows(width);
        break;
    }

    this.listMaxScroll = Math.max(0, contentHeight - this.listHeight);
    this.listScrollY = Math.min(this.listScrollY, this.listMaxScroll);
    this.applyListScroll();
  }

  private renderStatusMessage(width: number, message: string, actions: string[]): number {
    const text = this.add
      .text(0, 20, message, {
        fontFamily: "Arial, sans-serif",
        fontSize: "15px",
        fontStyle: "bold",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: width - 20 },
      })
      .setOrigin(0.5, 0);
    this.listContainer.add(text);

    let y = text.y + text.height + 20;
    for (const action of actions) {
      const button = createButton(
        this,
        0,
        y,
        action,
        () => {
          if (action === "ENABLE ONLINE FEATURES") {
            this.onlineModal.setVisible(true);
          } else {
            void this.refresh();
          }
        },
        { width: 220, height: 46 },
      );
      this.listContainer.add(button);
      y += 60;
    }
    return y;
  }

  private renderRows(width: number): number {
    const headerY = 0;
    const header = this.add
      .text(-width / 2, headerY, "RANK", { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
      .setOrigin(0, 0.5);
    this.listContainer.add(header);

    const entries: { rank: number; name: string; score: number; stars?: number }[] =
      this.tab === "level"
        ? this.levelEntries.map((e) => ({ rank: e.rank, name: e.displayName, score: e.score, stars: e.stars }))
        : this.worldEntries.map((e) => ({ rank: e.rank, name: e.displayName, score: e.totalScore }));

    let y = 20;
    const myRank = this.tab === "level" ? this.myLevelRank?.rank : this.myWorldRank?.rank;
    for (const entry of entries) {
      this.renderRow(width, y, entry.rank, entry.name, entry.score, entry.stars, entry.rank === myRank);
      y += ROW_HEIGHT;
    }

    y += 10;
    return Math.max(y, this.renderPersonalRow(width, y));
  }

  private renderRow(width: number, y: number, rank: number, name: string, score: number, stars: number | undefined, isMe: boolean): void {
    if (isMe) {
      const bg = this.add.rectangle(0, y + ROW_HEIGHT / 2 - 4, width, ROW_HEIGHT - 4, GameConfig.colors.accent, 0.12);
      this.listContainer.add(bg);
    }

    const rankText = this.add
      .text(-width / 2, y, `#${rank}`, { fontFamily: "Arial, sans-serif", fontSize: "14px", fontStyle: "bold", color: "#8b93a7" })
      .setOrigin(0, 0);
    this.listContainer.add(rankText);

    const nameText = this.add
      .text(-width / 2 + 46, y, name, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#f5f7fa",
        wordWrap: { width: width - 190 },
      })
      .setOrigin(0, 0);
    this.listContainer.add(nameText);

    const scoreText = this.add
      .text(width / 2 - (stars !== undefined ? 60 : 0), y, `${score}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(1, 0);
    this.listContainer.add(scoreText);

    if (stars !== undefined) {
      const starsGraphics = this.add.graphics();
      for (let i = 0; i < 3; i++) {
        drawStar(starsGraphics, width / 2 - 46 + i * 16, y + 8, 6, i < stars ? GameConfig.colors.star : GameConfig.colors.starEmpty);
      }
      this.listContainer.add(starsGraphics);
    }
  }

  private renderPersonalRow(width: number, y: number): number {
    const rank = this.tab === "level" ? this.myLevelRank : this.myWorldRank;
    if (!rank || !rank.hasScore) return y;
    // Already shown highlighted within the Top 10 list - no separate row needed.
    const isInTop10 = rank.rank !== null && rank.rank <= 10;
    if (isInTop10) return y;

    const label = this.add
      .text(-width / 2, y, "YOU", { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
      .setOrigin(0, 0);
    this.listContainer.add(label);
    y += 16;

    const score = this.tab === "level" ? (rank as MyLevelRank).score : (rank as MyWorldRank).totalScore;
    const stars = this.tab === "level" ? (rank as MyLevelRank).stars : undefined;
    this.renderRow(width, y, rank.rank ?? 0, this.myDisplayName ?? "ANONYMOUS", score, stars, true);
    return y + ROW_HEIGHT;
  }
}
